import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Archive, RefreshCw, Search, ShieldCheck, Download, Eye } from 'lucide-react';
import { API } from '../App';
import { useAuth } from '../contexts/AuthContext';
import { getRoleBadgeClass, getRoleLabel } from '../lib/permissions';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { SHOW_INSTITUTO_BW_BRANDING } from '../lib/branding';
import { appTodayDate } from '../lib/appDateTime';

const EMPTY_FILTERS = {
  search: '',
  animal_identifier: '',
  actor_email: '',
  action: '',
  entity_type: '',
  success: 'all',
  source: 'active',
  start_date: '',
  end_date: '',
};

const formatDateTime = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('pt-BR');
  } catch (error) {
    return value;
  }
};

const formatDetails = (details) => {
  if (!details || typeof details !== 'object' || Object.keys(details).length === 0) {
    return 'Sem detalhes adicionais';
  }
  return JSON.stringify(details, null, 2);
};

const getRequestErrorMessage = (error, fallback) => {
  const status = error.response?.status;
  const detail = error.response?.data?.detail;
  if (status && detail) return `${fallback}: ${status} - ${detail}`;
  if (status) return `${fallback}: erro ${status}`;
  return fallback;
};

export default function AuditLogsPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [logs, setLogs] = useState([]);
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const fetchLogs = async (activeFilters = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(activeFilters).forEach(([key, value]) => {
        if (!value || value === 'all') return;
        params.append(key, value);
      });
      params.set('limit', activeFilters.animal_identifier ? '200' : '100');
      const response = await axios.get(`${API}/audit-logs?${params.toString()}`);
      setLogs(response.data || []);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Erro ao carregar auditoria'));
    } finally {
      setLoading(false);
    }
  };

  const fetchArchives = async () => {
    try {
      const response = await axios.get(`${API}/audit-log-archives`);
      setArchives(response.data || []);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Erro ao carregar arquivos arquivados'));
    }
  };

  useEffect(() => {
    fetchLogs(EMPTY_FILTERS);
    fetchArchives();
  }, []);

  const metrics = useMemo(() => {
    const total = logs.length;
    const success = logs.filter((item) => item.success).length;
    const failure = total - success;
    const users = new Set(logs.map((item) => item.actor_user_id).filter(Boolean)).size;
    const archived = logs.filter((item) => item.archived).length;
    return { total, success, failure, users, archived };
  }, [logs]);

  const handleFilterChange = (field, value) => {
    setFilters((current) => ({ ...current, [field]: value }));
  };

  const handleApplyFilters = () => {
    fetchLogs(filters);
  };

  const handleResetFilters = () => {
    setFilters(EMPTY_FILTERS);
    fetchLogs(EMPTY_FILTERS);
  };

  const handleExportCsv = () => {
    const rows = [
      ['data_hora', 'usuario', 'email', 'perfil', 'acao', 'entidade', 'id_entidade', 'sucesso', 'detalhes'],
      ...logs.map((item) => [
        formatDateTime(item.created_at),
        item.actor_name || '',
        item.actor_email || '',
        item.actor_role || '',
        item.action || '',
        item.entity_type || '',
        item.entity_id || '',
        item.success ? 'Sim' : 'Não',
        JSON.stringify(item.details || {}),
      ]),
    ];
    const csv = rows
      .map((row) => row.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(';'))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `auditoria_${appTodayDate()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleArchiveOldLogs = async () => {
    if (!window.confirm('Arquivar logs antigos e remover do Atlas após confirmar o arquivo?')) return;
    setArchiveLoading(true);
    try {
      const response = await axios.post(`${API}/maintenance/storage/archive-audit-logs`, {
        older_than_days: 90,
        keep_recent: 1000,
        max_logs: 5000,
        delete_after_archive: true,
        dry_run: false,
      });
      const archived = response.data?.archived || 0;
      const deleted = response.data?.deleted || 0;
      toast.success(`Arquivados ${archived} logs; removidos ${deleted} do Atlas`);
      await fetchArchives();
      await fetchLogs(filters);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Erro ao arquivar logs antigos'));
    } finally {
      setArchiveLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-100">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="hidden md:flex items-center justify-between gap-6">
            <div className="flex items-center gap-3 min-w-0">
              {SHOW_INSTITUTO_BW_BRANDING && (
                <img src="/bw-symbol-final.png" alt="Instituto BW" className="h-12 w-12 object-contain" />
              )}
              <div className={`min-w-0 ${SHOW_INSTITUTO_BW_BRANDING ? 'border-l border-slate-200 pl-3' : ''}`}>
                <h1 className="text-xl font-bold text-slate-900 truncate">Auditoria do Sistema</h1>
                <p className="text-xs text-slate-500 truncate">Rastreamento completo por login, ação, entidade e período</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => fetchLogs()}><RefreshCw className="w-4 h-4 mr-2" />Atualizar</Button>
              <Button variant="outline" onClick={handleExportCsv}><Download className="w-4 h-4 mr-2" />Exportar CSV</Button>
              <Button variant="outline" onClick={handleArchiveOldLogs} disabled={archiveLoading}><Archive className="w-4 h-4 mr-2" />Arquivar antigos</Button>
              <Button variant="outline" onClick={() => navigate('/')}>Voltar</Button>
              <Button variant="ghost" className="text-red-600" onClick={logout}>Sair</Button>
            </div>
          </div>

          <div className="md:hidden space-y-4">
            <div className="flex items-start gap-3">
              {SHOW_INSTITUTO_BW_BRANDING && (
                <img src="/bw-symbol-final.png" alt="Instituto BW" className="h-12 w-12 object-contain flex-shrink-0" />
              )}
              <div className={`min-w-0 ${SHOW_INSTITUTO_BW_BRANDING ? 'border-l border-slate-200 pl-3' : ''}`}>
                <h1 className="text-xl font-bold text-slate-900 leading-tight">Auditoria do Sistema</h1>
                <p className="text-xs text-slate-500">Rastreamento completo por usuário</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => fetchLogs()}><RefreshCw className="w-4 h-4 mr-2" />Atualizar</Button>
              <Button variant="outline" onClick={handleExportCsv}><Download className="w-4 h-4 mr-2" />CSV</Button>
              <Button variant="outline" onClick={handleArchiveOldLogs} disabled={archiveLoading}><Archive className="w-4 h-4 mr-2" />Arquivar</Button>
              <Button variant="outline" onClick={() => navigate('/')}>Voltar</Button>
              <Button variant="ghost" className="text-red-600" onClick={logout}>Sair</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <section className="grid grid-cols-2 xl:grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">Eventos</p>
              <p className="text-3xl font-bold text-slate-900 mt-2">{metrics.total}</p>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 bg-emerald-50/70">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wide text-emerald-700">Sucessos</p>
              <p className="text-3xl font-bold text-emerald-800 mt-2">{metrics.success}</p>
            </CardContent>
          </Card>
          <Card className="border-rose-200 bg-rose-50/70">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wide text-rose-700">Falhas</p>
              <p className="text-3xl font-bold text-rose-800 mt-2">{metrics.failure}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">Arquivados na busca</p>
              <p className="text-3xl font-bold text-slate-900 mt-2">{metrics.archived}</p>
            </CardContent>
          </Card>
        </section>

        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg text-slate-900">Filtros de auditoria</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Busca geral</Label>
                <Input value={filters.search} onChange={(event) => handleFilterChange('search', event.target.value)} placeholder="ação, entidade, usuário..." />
              </div>
              <div className="space-y-2">
                <Label>Animal / ID IBAMA</Label>
                <Input value={filters.animal_identifier} onChange={(event) => handleFilterChange('animal_identifier', event.target.value)} placeholder="AVE 1366 ou ID do cadastro" />
              </div>
              <div className="space-y-2">
                <Label>Email do usuário</Label>
                <Input value={filters.actor_email} onChange={(event) => handleFilterChange('actor_email', event.target.value)} placeholder="usuario@exemplo.com" />
              </div>
              <div className="space-y-2">
                <Label>Ação</Label>
                <Input value={filters.action} onChange={(event) => handleFilterChange('action', event.target.value)} placeholder="users.update" />
              </div>
              <div className="space-y-2">
                <Label>Entidade</Label>
                <Input value={filters.entity_type} onChange={(event) => handleFilterChange('entity_type', event.target.value)} placeholder="user, exam, animal..." />
              </div>
              <div className="space-y-2">
                <Label>Período inicial</Label>
                <Input type="date" value={filters.start_date} onChange={(event) => handleFilterChange('start_date', event.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Período final</Label>
                <Input type="date" value={filters.end_date} onChange={(event) => handleFilterChange('end_date', event.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={filters.success} onValueChange={(value) => handleFilterChange('success', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="true">Sucesso</SelectItem>
                    <SelectItem value="false">Falha</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Origem dos dados</Label>
                <Select value={filters.source} onValueChange={(value) => handleFilterChange('source', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Atlas atual" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Atlas atual</SelectItem>
                    <SelectItem value="archived">Arquivados</SelectItem>
                    <SelectItem value="all">Atlas + arquivados</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={handleApplyFilters}><Search className="w-4 h-4 mr-2" />Aplicar filtros</Button>
              <Button variant="outline" onClick={handleResetFilters}>Limpar filtros</Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg text-slate-900">Arquivos arquivados disponíveis</CardTitle>
          </CardHeader>
          <CardContent>
            {archives.length > 0 ? (
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
                {archives.slice(0, 6).map((archive) => (
                  <div key={archive.id} className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-slate-900 truncate">{archive.file_name}</p>
                      <Badge variant="outline">{archive.storage_provider}</Badge>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      {formatDateTime(archive.period_start)} até {formatDateTime(archive.period_end)}
                    </p>
                    <p className="text-xs text-slate-600 mt-1">{archive.log_count || 0} eventos</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-500">
                Nenhum arquivo de auditoria arquivado ainda.
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg text-slate-900">Histórico por login e ações executadas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="hidden lg:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/Hora</TableHead>
                    <TableHead>Usuário</TableHead>
                    <TableHead>Perfil</TableHead>
                    <TableHead>Ação</TableHead>
                    <TableHead>Entidade</TableHead>
                    <TableHead>Resultado</TableHead>
                    <TableHead className="text-right">Detalhes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={`${log.storage_source || 'atlas'}-${log.id}`}>
                      <TableCell>{formatDateTime(log.created_at)}</TableCell>
                      <TableCell>
                        <div className="font-medium text-slate-900">{log.actor_name || 'Sistema'}</div>
                        <div className="text-xs text-slate-500">{log.actor_email || '-'}</div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getRoleBadgeClass(log.actor_role)}>{getRoleLabel(log.actor_role || 'sistema')}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{log.action}</TableCell>
                      <TableCell>
                        <div className="font-medium text-slate-900">{log.entity_type}</div>
                        <div className="text-xs text-slate-500">{log.entity_id}</div>
                      </TableCell>
                      <TableCell>
                        <Badge className={log.success ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}>
                          {log.success ? 'Sucesso' : 'Falha'}
                        </Badge>
                        {log.archived && <Badge variant="outline" className="ml-2">Arquivo</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" onClick={() => setSelectedLog(log)}>
                          <Eye className="w-4 h-4 mr-2" />
                          Ver
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!loading && logs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-10 text-slate-500">
                        Nenhum evento encontrado para os filtros selecionados.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="lg:hidden space-y-3">
              {logs.map((log) => (
                <div key={`${log.storage_source || 'atlas'}-${log.id}`} className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-900">{log.actor_name || 'Sistema'}</p>
                      <p className="text-xs text-slate-500">{log.actor_email || '-'}</p>
                    </div>
                    <Badge className={log.success ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}>
                      {log.success ? 'Sucesso' : 'Falha'}
                    </Badge>
                    {log.archived && <Badge variant="outline">Arquivo</Badge>}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Badge className={getRoleBadgeClass(log.actor_role)}>{getRoleLabel(log.actor_role || 'sistema')}</Badge>
                    <Badge variant="outline" className="font-mono text-[11px]">{log.action}</Badge>
                  </div>
                  <div className="text-sm text-slate-600">
                    <p><span className="font-medium">Quando:</span> {formatDateTime(log.created_at)}</p>
                    <p><span className="font-medium">Entidade:</span> {log.entity_type} / {log.entity_id}</p>
                  </div>
                  <Button variant="outline" className="w-full" onClick={() => setSelectedLog(log)}>
                    <Eye className="w-4 h-4 mr-2" />
                    Ver detalhes
                  </Button>
                </div>
              ))}
              {!loading && logs.length === 0 && (
                <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-slate-500">
                  Nenhum evento encontrado para os filtros selecionados.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>

      <Dialog open={Boolean(selectedLog)} onOpenChange={(open) => !open && setSelectedLog(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5" />
              Detalhes da auditoria
            </DialogTitle>
            <DialogDescription>
              Evento completo registrado para rastreabilidade do sistema.
            </DialogDescription>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div className="rounded-lg bg-slate-50 p-3 border border-slate-200">
                  <p className="font-medium text-slate-900">Usuário</p>
                  <p className="text-slate-600">{selectedLog.actor_name || 'Sistema'}</p>
                  <p className="text-xs text-slate-500">{selectedLog.actor_email || '-'}</p>
                </div>
                <div className="rounded-lg bg-slate-50 p-3 border border-slate-200">
                  <p className="font-medium text-slate-900">Execução</p>
                  <p className="text-slate-600">{selectedLog.action}</p>
                  <p className="text-xs text-slate-500">{formatDateTime(selectedLog.created_at)}</p>
                  {selectedLog.archived && <p className="text-xs text-slate-500">Origem: arquivo arquivado</p>}
                </div>
              </div>
              <div className="rounded-lg bg-slate-950 text-slate-100 p-4 overflow-auto">
                <pre className="text-xs whitespace-pre-wrap break-words">{formatDetails(selectedLog.details)}</pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
